import type { RuntimeType, SessionSummary, TranscriptRun } from '@deskworks/shared-types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { BrainCircuit, ChevronLeft, ClipboardList, MessageCircleQuestion, MessagesSquare, ScaleIcon, SquareTerminal } from 'lucide-react';
import type { ReactNode } from 'react';
import { RelativeTime } from '@/components/relative-time';
import { ExecutionStatusBadge } from '@/components/status-badge';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AgentConversation } from '@/components/work-item/agent-session';
import { api } from '@/lib/api';
import { formatAbsolute, formatDuration } from '@/lib/format';
import { useDesks, useWorkspaceId } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { EXECUTION_STATUS } from '@/lib/labels';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

const ALL = '__all__';

/** What actually ran the agent, in the words people know from the terminal. */
const ENGINE: Record<RuntimeType, string> = {
  claude_subscription: 'Claude Code',
  codex_subscription: 'Codex CLI',
  anthropic_api: t('the Anthropic API'),
  openai_api: t('the OpenAI API'),
  local: t('the demo runtime (no AI)'),
};

/**
 * Every agent run of the workspace — Lead evaluations, chats, Work Items,
 * reviews, follow-ups — and, for the selected one, what its runtime did step
 * by step, like watching the CLI.
 */
export function SessionsPage() {
  const workspaceId = useWorkspaceId();
  const { session, deskId } = useSearch({ from: '/_authed/w/$workspaceId/sessions' });
  const navigate = useNavigate({ from: '/w/$workspaceId/sessions' });
  const desks = useDesks(workspaceId);

  const list = useQuery({
    queryKey: qk.sessions(workspaceId, deskId),
    queryFn: () => api.sessions.list(workspaceId, { deskId, limit: 200 }),
    placeholderData: keepPreviousData,
  });
  const select = (id: string | undefined) => void navigate({ search: (s) => ({ ...s, session: id }), replace: !!session && !!id });

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b bg-background px-4 py-3 sm:px-6">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold tracking-tight">{t('Sessions')}</h1>
          <p className="text-[13px] text-muted-foreground">
            {t('Every time an agent runs, it gets its own worker with a fresh runtime session. Pick one to see how it worked — live.')}
          </p>
        </div>
        <Select value={deskId ?? ALL} onValueChange={(v) => void navigate({ search: (s) => ({ ...s, deskId: v === ALL ? undefined : v }), replace: true })}>
          <SelectTrigger size="sm" className="w-56">
            <SelectValue placeholder={t('Desk')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('All desks')}</SelectItem>
            {(desks.data ?? []).map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </header>

      <div className="flex min-h-0 flex-1">
        <aside className={cn('min-h-0 w-full shrink-0 overflow-y-auto border-r md:w-80 lg:w-96', session && 'hidden md:block')}>
          {list.isPending ? (
            <div className="p-4">
              <LoadingRows rows={8} />
            </div>
          ) : list.isError ? (
            <div className="p-4">
              <ErrorState error={list.error} onRetry={() => void list.refetch()} />
            </div>
          ) : list.data.length === 0 ? (
            <EmptyState
              compact
              className="m-4"
              icon={<SquareTerminal />}
              title={t('No sessions yet')}
              description={t('As soon as a Desk runs, an agent works on a Work Item or answers a chat, its session shows up here.')}
            />
          ) : (
            <ul className="flex flex-col divide-y">
              {list.data.map((s) => (
                <li key={s.executionId}>
                  <SessionRow s={s} active={s.executionId === session} onSelect={() => select(s.executionId)} />
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className={cn('min-h-0 min-w-0 flex-1 overflow-y-auto', !session && 'hidden md:block')}>
          {session ? (
            <SessionDetail executionId={session} workspaceId={workspaceId} onBack={() => select(undefined)} />
          ) : (
            <EmptyState
              className="m-8"
              icon={<SquareTerminal />}
              title={t('Pick a session')}
              description={t('On the left: every run, newest first — which Desk started it, which agent ran, on which runtime.')}
            />
          )}
        </section>
      </div>
    </div>
  );
}

function kindMeta(s: SessionSummary): { icon: ReactNode; label: string; subject: string | null } {
  if (s.kind === 'desk_evaluation') return { icon: <BrainCircuit />, label: t('Lead evaluation'), subject: s.triggers.length ? t('woken by {triggers}', { triggers: s.triggers.join(', ') }) : null };
  // The Lead woken on a Work Item (docs/features/self-organizing-desks.md): it plans, starts runs, checks, closes.
  if (s.kind === 'lead') return { icon: <BrainCircuit />, label: t('Lead run'), subject: s.workItemTitle };
  if (s.kind === 'conversation') return { icon: <MessagesSquare />, label: t('Chat'), subject: s.conversationTitle };
  if (s.kind === 'follow_up') return { icon: <MessageCircleQuestion />, label: t('Follow-up chat'), subject: s.workItemTitle };
  if (s.workItemKind === 'review') return { icon: <ScaleIcon />, label: t('Review'), subject: s.parentWorkItemTitle ?? s.workItemTitle };
  return { icon: <ClipboardList />, label: t('Work Item'), subject: s.workItemTitle };
}

function SessionRow({ s, active, onSelect }: { s: SessionSummary; active: boolean; onSelect: () => void }) {
  const meta = kindMeta(s);
  const live = EXECUTION_STATUS[s.status]?.live;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/60',
        active && 'bg-primary/5 shadow-[inset_3px_0_0_var(--color-primary)] hover:bg-primary/10',
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full border bg-card text-muted-foreground [&_svg]:size-3.5',
          live && 'border-indigo-300 text-indigo-600 dark:border-indigo-800 dark:text-indigo-300',
        )}
      >
        {meta.icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-medium">{meta.label}</span>
          <span className="shrink-0 text-xs text-muted-foreground" title={formatAbsolute(s.createdAt, true)}>
            <RelativeTime value={s.createdAt} />
          </span>
        </span>
        {meta.subject && <span className="block truncate text-[13px]">{meta.subject}</span>}
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
          {s.deskName ?? t('Desk')} · {s.agentName ?? t('Agent')}
        </span>
        <span className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <ExecutionStatusBadge status={s.status} />
          {s.runtimeType && <span>{ENGINE[s.runtimeType] ?? s.runtimeType}</span>}
          <span>{s.steps === 1 ? t('1 step') : t('{n} steps', { n: s.steps })}</span>
        </span>
      </span>
    </button>
  );
}

function SessionDetail({ executionId, workspaceId, onBack }: { executionId: string; workspaceId: string; onBack: () => void }) {
  const run = useQuery({ queryKey: qk.session(executionId), queryFn: () => api.sessions.get(executionId) });
  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 px-4 py-5 sm:px-6">
      <button type="button" onClick={onBack} className="inline-flex w-fit items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground md:hidden">
        <ChevronLeft className="size-4" /> {t('All sessions')}
      </button>
      {run.isPending ? (
        <LoadingRows rows={6} />
      ) : run.isError ? (
        <ErrorState error={run.error} onRetry={() => void run.refetch()} />
      ) : (
        <>
          <SessionStory run={run.data} workspaceId={workspaceId} />
          <Card className="overflow-hidden">
            <AgentConversation
              runs={[run.data]}
              isPending={false}
              error={null}
              onRetry={() => void run.refetch()}
              showRunHeader={false}
              emptyDescription={t('Nothing recorded for this run.')}
            />
          </Card>
        </>
      )}
    </div>
  );
}

/** Plain words: who started this, which agent ran on what, and what it was about. */
function SessionStory({ run, workspaceId }: { run: TranscriptRun; workspaceId: string }) {
  const meta = kindMeta(run);
  const engine = run.runtimeType ? (ENGINE[run.runtimeType] ?? run.runtimeType) : t('its runtime');
  const desk = (
    <Link to="/w/$workspaceId/desks/$deskId" params={{ workspaceId, deskId: run.deskId }} className="font-medium text-foreground hover:text-primary hover:underline">
      {run.deskName ?? t('the Desk')}
    </Link>
  );
  const agent = (
    <Link to="/w/$workspaceId/agents/$agentId" params={{ workspaceId, agentId: run.agentId }} className="font-medium text-foreground hover:text-primary hover:underline">
      {run.agentName ?? t('The agent')}
    </Link>
  );
  const item = run.workItemId && (
    <Link to="/w/$workspaceId/work-items/$workItemId" params={{ workspaceId, workItemId: run.workItemId }} className="font-medium text-foreground hover:text-primary hover:underline">
      {run.workItemTitle ?? t('Work Item')}
    </Link>
  );

  const triggers = run.triggers.length > 0 ? ` (${run.triggers.join(', ')})` : '';

  let story: ReactNode;
  if (run.kind === 'desk_evaluation') {
    story = tr(
      '{desk} woke its Lead Agent {agent}{triggers}. The Lead looks at the Desk — Goals, Playbook, open work, new events — and decides whether to create, continue or leave Work Items. It doesn’t do the work itself.',
      { desk, agent, triggers },
    );
  } else if (run.kind === 'lead') {
    story = tr(
      '{desk}’s Lead {agent} was woken on {item}{triggers}. The Lead owns the Work Item: it plans it, starts the agents’ runs with a brief, checks their results and closes it — between its runs it sleeps.',
      { desk, agent, item, triggers },
    );
  } else if (run.kind === 'conversation') {
    story = tr(
      '{agent} answered the chat {chat} on {desk}. A chat worker stays awake a few minutes for the next message; after that, the next message starts a new session that gets the chat history again.',
      {
        agent,
        desk,
        chat: run.conversationId ? (
          <Link
            to="/w/$workspaceId/conversations/$conversationId"
            params={{ workspaceId, conversationId: run.conversationId }}
            className="font-medium text-foreground hover:text-primary hover:underline"
          >
            {t('“{title}”', { title: run.conversationTitle ?? t('Conversation') })}
          </Link>
        ) : null,
      },
    );
  } else if (run.kind === 'follow_up') {
    story = tr(
      '{agent} answered questions about the finished Work Item {item} on {desk}. Follow-ups only read; if you ask for changes, the agent reopens the item.',
      { agent, item, desk },
    );
  } else if (run.workItemKind === 'review') {
    story = tr(
      '{agent} reviewed a proposal for “{parent}” on {desk} ({item}). Reviewers only read and end with a decision — approved or changes requested — that goes back into the other agent’s chat.',
      { agent, parent: run.parentWorkItemTitle ?? t('another Work Item'), desk, item },
    );
  } else {
    story = tr(
      '{agent} worked on the Work Item {item} on {desk}. It plans, uses its tools and connections, reports progress, and ends with a result, a question or a request for approval.',
      { agent, item, desk },
    );
  }

  return (
    <Card className="flex gap-3 p-4">
      <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary [&_svg]:size-4.5">{meta.icon}</span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold">{meta.label}</h2>
          <ExecutionStatusBadge status={run.status} />
        </div>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{story}</p>
        <p className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>
            {tr('Ran on {engine}', { engine: <span className="text-foreground">{engine}</span> })}
            {run.model && <span className="font-mono"> · {run.model}</span>}
            {run.runtimeName && <> · {t('runtime “{name}”', { name: run.runtimeName })}</>}
          </span>
          {run.startedAt && (
            <span title={formatAbsolute(run.startedAt, true)}>
              {tr('started {time}', { time: <RelativeTime value={run.startedAt} /> })} · {formatDuration(run.startedAt, run.finishedAt)}
            </span>
          )}
          <span>{run.entries.length === 1 ? t('1 step') : t('{n} steps', { n: run.entries.length })}</span>
          <span className="font-mono">{run.executionId.slice(0, 8)}</span>
        </p>
      </div>
    </Card>
  );
}
